import React, { ChangeEvent, InputHTMLAttributes, useEffect, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Color } from "types/types";
import { colors } from "utils/constants";
import { HEX_COLOR_REGEX } from "utils/regex";
import timeout from "utils/timeout";
import { formatHexColor } from "utils/utils";
import "./ColorPicker.scss";

export type ColorPickerProps = Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> & {
  onValueChange: (e: ChangeEvent<HTMLInputElement> | DropdownChangeEvent) => void;
  disabled?: boolean;
  enableTransparent?: boolean;
};

const TRANSPARENT_OPTION = { hex: "transparent", name: "None" };

const ColorPicker: React.FC<ColorPickerProps> = props => {
  const { onValueChange, value: initValue, enableTransparent, ...restProps } = props;

  const normalized = typeof initValue === "string" ? initValue : "";

  const initial = enableTransparent ? normalized || "transparent" : formatHexColor(normalized) || "#000000";

  const [colorText, setColorText] = useState(initial);

  useEffect(() => {
    if (enableTransparent) {
      setColorText(initValue ? String(initValue) : "transparent");
    } else {
      setColorText(formatHexColor(initValue as string));
    }
  }, [initValue, enableTransparent]);

  const handleChange = (e: ChangeEvent<HTMLInputElement> | DropdownChangeEvent): void => {
    let raw = e.target.value;

    if (enableTransparent && raw === "transparent") {
      setColorText("transparent");
      onValueChange(e);
      return;
    }

    if (raw.length > 0) raw = formatHexColor(raw);
    if ((!HEX_COLOR_REGEX.test(raw) && raw.length > 0) || raw.length > 7) return;

    setColorText(raw);

    timeout(() => {
      if (raw.length === 7) onValueChange(e);
    }, 300);
  };

  const colorItemTemplate = (option: Color | typeof TRANSPARENT_OPTION) => {
    if (enableTransparent && option.hex === "transparent") {
      return <span className="color-transparent-swatch">None</span>;
    }
    return <span style={{ border: `7px solid ${option.hex}` }}>{option.hex}</span>;
  };

  return (
    <div className="color-picker p-inputgroup flex">
      <Dropdown
        value={colorText}
        onChange={handleChange}
        options={enableTransparent ? [TRANSPARENT_OPTION, ...colors] : colors}
        optionLabel="hex"
        optionValue="hex"
        itemTemplate={colorItemTemplate}
        className="p-inputtext-sm align-items-center"
        onFocus={e => e.target.select()}
        id={restProps.id}
        editable
        name={restProps.name}
        disabled={props.disabled}
      />

      <input
        type="color"
        {...restProps}
        value={colorText.length === 7 ? colorText : "#ffffff"}
        onChange={handleChange}
        className="p-inputgroup-addon"
        disabled={props.disabled}
      />
    </div>
  );
};

export default ColorPicker;
